export const CROSS_TAB_LOGOUT_KEY = 'onevest-cross-tab-logout';
export const SESSION_START_KEY = 'onevest-session-start';
const LAST_PROCESSED_KEY = 'onevest-last-processed-logout';

export const broadcastLogout = (): number => {
  const logoutTs = Date.now();
  localStorage.setItem(CROSS_TAB_LOGOUT_KEY, String(logoutTs));
  markLogoutBroadcastProcessed(logoutTs);
  return logoutTs;
};

/** Read before `localStorage.clear()` so a prior `broadcastLogout` survives the wipe. */
export const captureLogoutBroadcastTimestamp = (): string | null =>
  localStorage.getItem(CROSS_TAB_LOGOUT_KEY);

/** Restore a captured timestamp or broadcast a new one after `localStorage.clear()`. */
export const restoreOrBroadcastLogout = (preserved: string | null): void => {
  if (preserved) {
    localStorage.setItem(CROSS_TAB_LOGOUT_KEY, preserved);
    return;
  }

  broadcastLogout();
};

export const markSessionStart = (): void => {
  localStorage.setItem(SESSION_START_KEY, String(Date.now()));
};

export const invalidateSessionEpoch = (): void => {
  localStorage.removeItem(SESSION_START_KEY);
};

const getSessionStart = (): number =>
  Number(localStorage.getItem(SESSION_START_KEY) || 0);

export const isLogoutBroadcastPending = (): boolean => {
  const logoutTs = Number(localStorage.getItem(CROSS_TAB_LOGOUT_KEY) || 0);
  return logoutTs > getSessionStart();
};

export const shouldProcessLogoutBroadcast = (logoutTs: number): boolean => {
  if (!logoutTs || logoutTs <= getSessionStart()) {
    return false;
  }

  const lastProcessed = Number(sessionStorage.getItem(LAST_PROCESSED_KEY) || 0);
  return logoutTs > lastProcessed;
};

export const markLogoutBroadcastProcessed = (logoutTs: number): void => {
  sessionStorage.setItem(LAST_PROCESSED_KEY, String(logoutTs));
};

export const isAtLogoutLanding = (
  currentLocation: Pick<Location, 'href' | 'origin' | 'pathname'>,
  target: string,
): boolean => {
  const targetUrl = new URL(target, currentLocation.href);
  return (
    currentLocation.origin === targetUrl.origin &&
    currentLocation.pathname === targetUrl.pathname
  );
};

/** Hard-navigate listener tabs to the org login landing after cross-tab logout. */
export const navigateToLogoutLanding = (target: string): void => {
  const targetUrl = new URL(target, window.location.href);

  if (isAtLogoutLanding(window.location, targetUrl.toString())) {
    window.location.reload();
  } else {
    window.location.replace(targetUrl.toString());
  }
};

export const subscribeToCrossTabLogout = (
  onLogout: (logoutTs: number) => void,
): (() => void) => {
  const handler = (event: StorageEvent) => {
    if (event.key !== CROSS_TAB_LOGOUT_KEY || !event.newValue) {
      return;
    }

    onLogout(Number(event.newValue));
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
};
